import { useState, useEffect } from "react";
import { fetchDeploymentHistory } from "./deploymentService";
import { DeploymentInfo } from "./Index";
import Logger from "../../utils/logger";

export const useDeploymentHistory = (repo: string, env: string) => {
  const [deploymentsLog, setDeploymentsLog] = useState<DeploymentInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] =
    useState<string>(searchQuery);

  // Fetch deployment history
  useEffect(() => {
    Logger.info("Fetching deployment history", { repo, env, page, pageSize });

    setLoading(true);
    setError(null);

    fetchDeploymentHistory(repo, env, page, pageSize)
      .then((data) => {
        Logger.info("Deployment history fetched successfully", { data });
        setDeploymentsLog(data);
      })
      .catch((err) => {
        Logger.error("Error fetching deployment history", { error: err });
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
        Logger.info("Fetching deployment history completed");
      });
  }, [repo, env, page, pageSize]);

  // Debounce search query
  useEffect(() => {
    Logger.info("Setting up debounced search query", { searchQuery });

    const handler = setTimeout(() => {
      Logger.info("Debounced search query updated", {
        debouncedSearchQuery: searchQuery,
      });
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      Logger.info("Clearing debounced search query timeout");
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const filteredData = deploymentsLog.filter((item) =>
    item.deployed_by.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
  );

  Logger.info("Filtered deployment data", { filteredData });

  return {
    deploymentsLog: filteredData,
    loading,
    error,
    page,
    pageSize,
    setPage,
    searchQuery,
    setSearchQuery,
    isPrevDisabled: page === 1,
    isNextDisabled: deploymentsLog.length < pageSize,
  };
};
